import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FolderPlus,
  Plus,
  Home,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { erpKnowledgeBaseService } from '@/services/erpKnowledgeBaseService';
import { useUserRoles } from '@/hooks/useUserRoles';
import { logger } from '@/utils/logger';
interface FolderData {
  id: string;
  name: string;
  description?: string;
  children_count: number;
  parent_id?: string;
}

interface MobileFolderItemProps {
  folder: FolderData;
  level: number;
  isExpanded: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  onToggle: () => void;
  onAddChild: () => void;
  onEdit: () => void;
  onDelete: () => void;
  renderChildren?: () => React.ReactNode;
}

const MobileFolderItem: React.FC<MobileFolderItemProps> = ({
  folder,
  level,
  isExpanded,
  isLoading,
  isAdmin,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
  renderChildren,
}) => {
  const [showActions, setShowActions] = useState(false);
  const paddingLeft = level * 20;
  const hasChildren = folder.children_count > 0;

  return (
    <div>
      <div
        className="flex items-center py-3 px-3 bg-muted rounded-lg mb-2 active:bg-accent transition-colors"
        style={{ marginLeft: `${paddingLeft}px` }}
        onClick={onToggle}
      >
        {/* Expand/Collapse Arrow */}
        <div className="w-6 h-6 flex items-center justify-center mr-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )
          ) : (
            <span className="w-5" />
          )}
        </div>

        {/* Folder Icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0" />
        ) : (
          <Folder className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0" />
        )}

        {/* Folder Name */}
        <span className="text-sm text-foreground font-medium flex-1 truncate">{folder.name}</span>

        {/* Action toggle button for admin */}
        {isAdmin && (
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <span className="text-muted-foreground text-lg">•••</span>
          </button>
        )}
      </div>

      {/* Action buttons row - visible when toggled */}
      {isAdmin && showActions && (
        <div
          className="flex items-center gap-2 mb-2 pb-2"
          style={{ marginLeft: `${paddingLeft + 12}px` }}
        >
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-muted-foreground border-border"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              setShowActions(false);
            }}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setShowActions(false);
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Delete
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 text-primary border-primary/30 hover:bg-primary/10"
            onClick={(e) => {
              e.stopPropagation();
              onAddChild();
              setShowActions(false);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      )}

      {/* Children */}
      {isExpanded && renderChildren && (
        <div className="ml-0">
          {renderChildren()}
        </div>
      )}
    </div>
  );
};

// Dialog for creating folders
interface FolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentName?: string;
  isLoading?: boolean;
  onSubmit: (name: string) => Promise<void>;
}

const FolderDialog: React.FC<FolderDialogProps> = ({
  open,
  onOpenChange,
  parentName,
  isLoading,
  onSubmit,
}) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      return;
    }
    await onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground w-[calc(100%-32px)] max-w-[calc(100%-32px)] rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">Create Section</DialogTitle>
          {parentName && (
            <DialogDescription>
              Creating inside: <span className="font-medium text-foreground">{parentName}</span>
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">Section name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter section name"
                className="bg-background border-input text-foreground"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button type="button" variant="outline" className="flex-1 border-border text-foreground" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/85">
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for editing folders
interface EditFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder?: FolderData;
  isLoading?: boolean;
  onSubmit: (name: string) => Promise<void>;
}

const EditFolderDialog: React.FC<EditFolderDialogProps> = ({
  open,
  onOpenChange,
  folder,
  isLoading,
  onSubmit,
}) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open && folder) {
      setName(folder.name);
    }
  }, [open, folder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      return;
    }
    await onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground w-[calc(100%-32px)] max-w-[calc(100%-32px)] rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">Edit Section</DialogTitle>
          <DialogDescription>
            Update the section name
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-foreground">Section name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter section name"
                className="bg-background border-input text-foreground"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button type="button" variant="outline" className="flex-1 border-border text-foreground" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/85">
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for delete confirmation
interface DeleteFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder?: FolderData;
  isLoading?: boolean;
  onConfirm: () => Promise<void>;
}

const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({
  open,
  onOpenChange,
  folder,
  isLoading,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground w-[calc(100%-32px)] max-w-[calc(100%-32px)] rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">Delete Section</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{folder?.name}"? This action cannot be undone.
            {folder && folder.children_count > 0 && (
              <span className="block mt-2 text-destructive font-medium">
                Warning: This section contains {folder.children_count} sub-section(s).
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row gap-2">
          <Button type="button" variant="outline" className="flex-1 border-border text-foreground" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            disabled={isLoading}
            onClick={onConfirm}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MobileKnowledgeAdmin: React.FC = () => {
  const { toast } = useToast();
  const { roles: userRoles, isSuperAdmin } = useUserRoles();
  const isAdmin = isSuperAdmin || userRoles.some(role => role.name === 'Admin');

  // Folder data stored by parent ID (empty string for root level)
  const [foldersByParent, setFoldersByParent] = useState<Map<string, FolderData[]>>(new Map());
  // Pagination state per parent folder
  const [paginationByParent, setPaginationByParent] = useState<Map<string, { page: number; hasNext: boolean; totalCount: number }>>(new Map());
  const [loadingMore, setLoadingMore] = useState<Set<string>>(new Set());

  // Loading and expanded state
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Dialog state
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    parentId?: string;
    parentName?: string;
  }>({ open: false });
  const [isCreating, setIsCreating] = useState(false);

  // Edit dialog state
  const [editDialogState, setEditDialogState] = useState<{
    open: boolean;
    folder?: FolderData;
  }>({ open: false });
  const [isEditing, setIsEditing] = useState(false);

  // Delete dialog state
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    folder?: FolderData;
  }>({ open: false });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch folders and documents for a given parent (empty string = root level)
  const fetchFolders = useCallback(async (parentId: string = '', page = 1) => {
    try {
      if (parentId) {
        setLoadingFolders(prev => new Set(prev).add(parentId));
      }
      if (page > 1) {
        setLoadingMore(prev => new Set(prev).add(parentId));
      }

      const result = await erpKnowledgeBaseService.getFolderContents(parentId || undefined, true, page);

      // Separate folders and documents
      const foldersData = (result.data || [])
        .filter((item: any) => item.is_folder === 1)
        .map((item: any) => ({
          id: item.name,
          name: item.document_name || item.name,
          description: item.description,
          children_count: item.folders_count ?? item.children_count ?? 0,
          parent_id: parentId,
        }));

      setFoldersByParent(prev => {
        const next = new Map(prev);
        if (page > 1) {
          const existing = next.get(parentId) || [];
          next.set(parentId, [...existing, ...foldersData]);
        } else {
          next.set(parentId, foldersData);
        }
        return next;
      });

      // Track pagination state
      if (result.pagination) {
        setPaginationByParent(prev => {
          const next = new Map(prev);
          next.set(parentId, {
            page: result.pagination!.page,
            hasNext: result.pagination!.has_next,
            totalCount: result.pagination!.total_count,
          });
          return next;
        });
      }

      return foldersData;
    } catch (error) {
      logger.error('Error fetching folders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load folders',
        variant: 'destructive',
      });
      return [];
    } finally {
      if (parentId) {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
      setLoadingMore(prev => {
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
      setIsInitialLoading(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    fetchFolders('');
  }, [fetchFolders]);

  // Toggle folder expansion (accordion - close siblings but keep parents open)
  const toggleFolder = useCallback(async (folderId: string) => {
    const isExpanded = expandedFolders.has(folderId);

    if (isExpanded) {
      // Close this folder and all its descendants
      setExpandedFolders(prev => {
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
    } else {
      // Fetch children if not already loaded
      if (!foldersByParent.has(folderId)) {
        await fetchFolders(folderId);
      }

      // Find the parent of this folder to get siblings
      const folder = Array.from(foldersByParent.values())
        .flat()
        .find(f => f.id === folderId);
      const parentId = folder?.parent_id ?? '';

      // Get sibling folder IDs (folders with same parent)
      const siblings = (foldersByParent.get(parentId) || []).map(f => f.id);

      // Close siblings but keep parents open, then open this one
      setExpandedFolders(prev => {
        const next = new Set(prev);
        // Remove siblings from expanded set
        siblings.forEach(siblingId => {
          if (siblingId !== folderId) {
            next.delete(siblingId);
          }
        });
        // Add the clicked folder
        next.add(folderId);
        return next;
      });
    }
  }, [expandedFolders, foldersByParent, fetchFolders]);

  // Open create dialog
  const openCreateDialog = (parentId?: string, parentName?: string) => {
    setDialogState({ open: true, parentId, parentName });
  };

  const closeDialog = () => {
    setDialogState({ open: false });
  };

  // Create folder handler
  const handleCreateFolder = async (name: string) => {
    try {
      setIsCreating(true);

      await erpKnowledgeBaseService.createItem({
        document_name: name,
        is_folder: true,
        parent_directory: dialogState.parentId,
      });

      toast({
        title: 'Success',
        description: 'Section created',
      });
      erpKnowledgeBaseService.clearCache();

      // Refresh the parent's children list
      const parentId = dialogState.parentId || '';
      await fetchFolders(parentId);

      // If there's a parent, expand it and refresh its parent to update count
      if (dialogState.parentId) {
        setExpandedFolders(prev => new Set(prev).add(dialogState.parentId!));

        // Find the parent folder to get its parent_id
        const parentFolder = Array.from(foldersByParent.values())
          .flat()
          .find(f => f.id === dialogState.parentId);

        if (parentFolder?.parent_id !== undefined) {
          await fetchFolders(parentFolder.parent_id);
        } else {
          // Parent is at root level, refresh root
          await fetchFolders('');
        }
      }

      closeDialog();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create section',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (folder: FolderData) => {
    setEditDialogState({ open: true, folder });
  };

  const closeEditDialog = () => {
    setEditDialogState({ open: false });
  };

  // Edit folder handler
  const handleEditFolder = async (name: string) => {
    if (!editDialogState.folder) return;

    try {
      setIsEditing(true);

      await erpKnowledgeBaseService.updateItem({
        name: editDialogState.folder.id,
        document_name: name,
      });

      toast({
        title: 'Success',
        description: 'Section updated',
      });
      erpKnowledgeBaseService.clearCache();

      // Refresh the parent's folder list
      const parentId = editDialogState.folder.parent_id || '';
      await fetchFolders(parentId);

      closeEditDialog();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update section',
        variant: 'destructive',
      });
    } finally {
      setIsEditing(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (folder: FolderData) => {
    setDeleteDialogState({ open: true, folder });
  };

  const closeDeleteDialog = () => {
    setDeleteDialogState({ open: false });
  };

  // Delete folder handler
  const handleDeleteFolder = async () => {
    if (!deleteDialogState.folder) return;

    try {
      setIsDeleting(true);

      await erpKnowledgeBaseService.deleteItem(deleteDialogState.folder.id);

      toast({
        title: 'Success',
        description: 'Section deleted',
      });
      erpKnowledgeBaseService.clearCache();

      // Remove from expanded set if it was expanded
      setExpandedFolders(prev => {
        const next = new Set(prev);
        next.delete(deleteDialogState.folder!.id);
        return next;
      });

      // Refresh the parent's folder list
      const parentId = deleteDialogState.folder.parent_id || '';
      await fetchFolders(parentId);

      closeDeleteDialog();
    } catch (error: any) {
      // Extract error message - service throws Error with API message
      const errorMessage = error?.message || 'Failed to delete section';
      toast({
        title: 'Unable to delete',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Load more folders for a given parent
  const loadMoreFolders = useCallback((parentId: string) => {
    const pagination = paginationByParent.get(parentId);
    if (pagination && pagination.hasNext) {
      fetchFolders(parentId, pagination.page + 1);
    }
  }, [paginationByParent, fetchFolders]);

  // Recursive folder renderer
  const renderFolders = (parentId: string, level: number): React.ReactNode => {
    const folders = foldersByParent.get(parentId) || [];
    const pagination = paginationByParent.get(parentId);
    const isLoadingMoreHere = loadingMore.has(parentId);
    const paddingLeft = level * 20;

    return (
      <>
        {folders.map(folder => (
          <MobileFolderItem
            key={folder.id}
            folder={folder}
            level={level}
            isExpanded={expandedFolders.has(folder.id)}
            isLoading={loadingFolders.has(folder.id)}
            isAdmin={isAdmin}
            onToggle={() => toggleFolder(folder.id)}
            onAddChild={() => openCreateDialog(folder.id, folder.name)}
            onEdit={() => openEditDialog(folder)}
            onDelete={() => openDeleteDialog(folder)}
            renderChildren={() => (
              <>
                {renderFolders(folder.id, level + 1)}
              </>
            )}
          />
        ))}
        {pagination?.hasNext && (
          <div style={{ marginLeft: `${paddingLeft}px` }} className="py-2 px-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadMoreFolders(parentId)}
              disabled={isLoadingMoreHere}
              className="text-xs text-primary hover:text-primary/80 hover:bg-primary/10"
            >
              {isLoadingMoreHere ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1.5" />
                  Load more
                </>
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  const rootFolders = foldersByParent.get('') || [];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header Section */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">
              Folder Manager
            </h1>
          </div>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openCreateDialog()}
              className="w-10 h-10 rounded-full bg-primary hover:bg-primary/85 text-primary-foreground"
            >
              <FolderPlus className="h-5 w-5" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">Create and manage knowledge base folders</p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {isInitialLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <span className="text-muted-foreground">Loading folders...</span>
          </div>
        ) : rootFolders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Folder className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-foreground text-center mb-2">No sections yet</p>
            <p className="text-muted-foreground text-sm text-center">
              {isAdmin ? 'Tap the + button to create a section' : 'Sections will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {renderFolders('', 0)}
          </div>
        )}
      </div>

      {/* Create Folder Dialog */}
      <FolderDialog
        open={dialogState.open}
        onOpenChange={(open) => !open && closeDialog()}
        parentName={dialogState.parentName}
        isLoading={isCreating}
        onSubmit={handleCreateFolder}
      />

      {/* Edit Folder Dialog */}
      <EditFolderDialog
        open={editDialogState.open}
        onOpenChange={(open) => !open && closeEditDialog()}
        folder={editDialogState.folder}
        isLoading={isEditing}
        onSubmit={handleEditFolder}
      />

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        open={deleteDialogState.open}
        onOpenChange={(open) => !open && closeDeleteDialog()}
        folder={deleteDialogState.folder}
        isLoading={isDeleting}
        onConfirm={handleDeleteFolder}
      />

    </div>
  );
};
